import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { RefreshCw, Copy, Download, Trash2, ExternalLink } from "react-feather";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../common/breadcrumb";
import {
  getPlatformInfo,
  getMoyasarOnboarding,
  createMoyasarMerchant,
  linkMoyasarMerchant,
  updateMoyasarMerchant,
  submitMoyasarReview,
  syncMoyasarMerchant,
  listMoyasarDocuments,
  uploadMoyasarDocument,
  deleteMoyasarDocument,
  downloadMoyasarDocument,
  STATUS_INFO,
  REASON_LABELS,
  SIGNATURE_LABELS,
} from "../../utils/moyasarPlatformApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C, reportingGlobalCSS, tbl } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import { StatusPill } from "../sales/moyasar-merchants";

// One merchant's onboarding as a Moyasar Platform sub-merchant: create (or link), documents,
// fees, submit for review, and the Nafath contract link. Everything shown comes from Moyasar
// through our API — the page never talks to Moyasar directly and never sees secret keys.

const TABS = [
  { key: "status", label: "Status" },
  { key: "profile", label: "Profile" },
  { key: "fees", label: "Fees" },
  { key: "documents", label: "Documents" },
];

const SCHEMES = [
  { value: "mada", label: "mada" },
  { value: "visa", label: "Visa" },
  { value: "master", label: "Mastercard" },
];
const METHODS = [
  { value: "creditcard", label: "Card" },
  { value: "applepay", label: "Apple Pay" },
  { value: "samsungpay", label: "Samsung Pay" },
  { value: "googlepay", label: "Google Pay" },
];
const SIGNATORY_LABELS = { owner: "The owner", commercial_contract: "Manager named in the articles", power_of_attorney: "Someone with a power of attorney" };
// Fee groups shown in the matrix. The first two are what Wags merchants use today.
const FEE_GROUPS = [
  { key: "mada", label: "mada" },
  { key: "cc", label: "Visa / Mastercard (local)" },
  { key: "cc_int", label: "International cards" },
  { key: "stcpay", label: "STC Pay" },
  { key: "amex", label: "Amex" },
  { key: "amex_int", label: "Amex (international)" },
];

const tabBtn = (active) => ({
  background: active ? C.coral : C.white,
  color: active ? C.white : C.textMed,
  border: `1.5px solid ${active ? C.coral : C.border}`,
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 13,
  padding: "7px 14px",
  cursor: "pointer",
});
const btn = (kind = "default", disabled = false) => ({
  padding: "8px 16px",
  borderRadius: 8,
  border: `1.5px solid ${kind === "primary" ? C.coral : kind === "danger" ? "#E74C3C" : C.border}`,
  background: kind === "primary" ? C.coral : C.white,
  color: kind === "primary" ? C.white : kind === "danger" ? "#E74C3C" : C.text,
  fontSize: 13,
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});
const inputStyle = { width: "100%", padding: "9px 12px", fontSize: 13, borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, outline: "none" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6 };
const errText = { fontSize: 11, color: "#C0392B", marginTop: 4 };

const Field = ({ label, hint, error, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {hint && !error && <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{hint}</div>}
    {error && <div style={errText}>{[].concat(error).join(", ")}</div>}
  </div>
);

const Checks = ({ options, value, onChange, disabled }) => (
  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
    {options.map((o) => (
      <label key={o.value} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: disabled ? "default" : "pointer" }}>
        <input
          type="checkbox"
          disabled={disabled}
          checked={(value || []).includes(o.value)}
          onChange={(e) => onChange(e.target.checked ? [...(value || []), o.value] : (value || []).filter((v) => v !== o.value))}
        />
        {o.label}
      </label>
    ))}
  </div>
);

// Moyasar's create errors use its own field names; map them onto our form's.
const CREATE_FIELD_MAP = { name: "legalName", public_name: "publicName", email: "email", admin_email: "email", website: "website", owners_count: "ownersCount", signatory: "signatory" };
const formErrors = (fieldErrors) => {
  const out = {};
  Object.entries(fieldErrors || {}).forEach(([k, v]) => {
    out[CREATE_FIELD_MAP[k] || k] = v;
  });
  return out;
};

// Same formula as the API's computeFee (Moyasar's docs: 148 SAR at 2.7% + 1 → 5.75).
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const computeFee = (amount, rate, fixed, cap, taxInclusive) => {
  let rated = round2(((Number(amount) || 0) * (Number(rate) || 0)) / 100);
  if (cap !== "" && cap !== null && cap !== undefined && Number.isFinite(Number(cap))) rated = Math.min(rated, Number(cap));
  const base = round2(rated + (Number(fixed) || 0));
  return round2(base + (taxInclusive ? 0 : base * 0.15));
};

// Moyasar returns fees as [{ name: "cc_charge", rate, fixed, rated_cap, tax_inclusive }].
const feesFromRemote = (fees) => {
  const out = {};
  (fees || []).forEach((f) => {
    if (!f?.name) return;
    out[`${f.name}_rate`] = f.rate ?? "";
    out[`${f.name}_fixed`] = f.fixed ?? "";
    if (f.rated_cap !== null && f.rated_cap !== undefined) out[`${f.name}_cap`] = f.rated_cap;
    if (typeof f.tax_inclusive === "boolean") out.tax_inclusive = f.tax_inclusive;
  });
  return out;
};

const FeesEditor = ({ fees, setFees, errors, disabled }) => {
  const [advanced, setAdvanced] = useState(false);
  const [amount, setAmount] = useState(100);
  const groups = advanced ? FEE_GROUPS : FEE_GROUPS.slice(0, 2);
  const cell = (key) => (
    <td style={{ ...tbl.td, padding: "6px 8px" }}>
      <input
        type="number"
        min="0"
        step="0.01"
        disabled={disabled}
        style={{ ...inputStyle, padding: "6px 8px", borderColor: errors[`fees.${key}`] ? "#E74C3C" : C.border }}
        value={fees[key] ?? ""}
        onChange={(e) => setFees({ ...fees, [key]: e.target.value })}
        title={errors[`fees.${key}`] ? [].concat(errors[`fees.${key}`]).join(", ") : key}
      />
    </td>
  );
  const taxInclusive = !!fees.tax_inclusive;
  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={tbl.table}>
          <thead>
            <tr>
              <th style={tbl.th}>Scheme</th>
              <th style={tbl.th}>Charge %</th>
              <th style={tbl.th}>Charge fixed (SAR)</th>
              <th style={tbl.th}>Charge cap (SAR)</th>
              <th style={tbl.th}>Refund %</th>
              <th style={tbl.th}>Refund fixed (SAR)</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key}>
                <td style={{ ...tbl.td, fontWeight: 600 }}>{g.label}</td>
                {cell(`${g.key}_charge_rate`)}
                {cell(`${g.key}_charge_fixed`)}
                {cell(`${g.key}_charge_cap`)}
                {cell(`${g.key}_refund_rate`)}
                {cell(`${g.key}_refund_fixed`)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {Object.entries(errors)
        .filter(([k]) => k.startsWith("fees."))
        .map(([k, v]) => (
          <div key={k} style={errText}>{k.replace("fees.", "")}: {[].concat(v).join(", ")}</div>
        ))}
      <div style={{ display: "flex", gap: 18, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" disabled={disabled} checked={taxInclusive} onChange={(e) => setFees({ ...fees, tax_inclusive: e.target.checked })} />
          Rates already include VAT
        </label>
        <button type="button" style={{ ...btn(), padding: "5px 10px", fontSize: 12 }} onClick={() => setAdvanced(!advanced)}>
          {advanced ? "Hide other schemes" : "Show other schemes"}
        </button>
      </div>
      <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: C.bg, fontSize: 13 }}>
        <span style={{ color: C.textMed }}>Preview — on a payment of </span>
        <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...inputStyle, width: 90, display: "inline-block", padding: "4px 8px" }} />
        <span style={{ color: C.textMed }}> SAR, Moyasar takes </span>
        <b>mada {computeFee(amount, fees.mada_charge_rate, fees.mada_charge_fixed, fees.mada_charge_cap, taxInclusive).toFixed(2)} SAR</b>
        <span style={{ color: C.textMed }}> · </span>
        <b>card {computeFee(amount, fees.cc_charge_rate, fees.cc_charge_fixed, fees.cc_charge_cap, taxInclusive).toFixed(2)} SAR</b>
        <span style={{ color: C.textMed }}>{taxInclusive ? " (VAT included)." : " (incl. 15% VAT on the fee)."}</span>
      </div>
    </div>
  );
};

const cleanFees = (fees) => {
  const out = {};
  Object.entries(fees || {}).forEach(([k, v]) => {
    if (k === "tax_inclusive") out[k] = !!v;
    else if (v !== "" && v !== null && v !== undefined) out[k] = Number(v);
  });
  return out;
};

// ─── Create / link (merchant not on Moyasar yet) ────────────────────────────

const CreateOrLink = ({ merchantId, platform, onDone, previous }) => {
  const [form, setForm] = useState({
    type: "establishment",
    legalName: previous?.legalName || "",
    publicName: previous?.publicName || "",
    email: previous?.adminEmail || "",
    website: previous?.website || "",
    ownersCount: 1,
    signatory: "owner",
    statementDescriptor: "",
    enabledSchemes: ["mada", "visa", "master"],
    paymentMethods: ["creditcard", "applepay"],
  });
  const [fees, setFees] = useState({ ...(platform?.standardFees || {}) });
  const [errors, setErrors] = useState(formErrors(previous?.lastError?.fieldErrors));
  const [saving, setSaving] = useState(false);
  const [linkId, setLinkId] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const create = async () => {
    setSaving(true);
    setErrors({});
    const res = await createMoyasarMerchant(merchantId, { ...form, ownersCount: Number(form.ownersCount), fees: cleanFees(fees) });
    setSaving(false);
    if (!res.ok) {
      setErrors(formErrors(res.error.fieldErrors));
      return errorToast(res.error.message);
    }
    successToast("Created on Moyasar. Next: upload the documents.");
    return onDone("documents");
  };

  const link = async () => {
    if (!linkId.trim()) return errorToast("Paste the Moyasar merchant ID");
    setSaving(true);
    const res = await linkMoyasarMerchant(merchantId, linkId.trim());
    setSaving(false);
    if (!res.ok) return errorToast(res.error.message);
    successToast("Linked");
    return onDone("status");
  };

  return (
    <Fragment>
      {previous?.status === "create_failed" && (
        <RCard>
          <div style={{ color: "#C0392B", fontWeight: 600 }}>The last create attempt was rejected by Moyasar: {previous?.lastError?.message}</div>
        </RCard>
      )}
      <RCard title="Create this merchant on Moyasar">
        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 16 }}>
          Moyasar emails a dashboard invitation to the email below as soon as the merchant is created. Use the legal name exactly as on the CR.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
          <Field label="Business type" error={errors.type}>
            <select style={inputStyle} value={form.type} onChange={(e) => set("type", e.target.value)}>
              {(platform?.merchantTypes || ["establishment", "company"]).map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </Field>
          <Field label="Legal name (exactly as on the CR)" error={errors.legalName}>
            <input style={inputStyle} dir="auto" value={form.legalName} onChange={(e) => set("legalName", e.target.value)} />
          </Field>
          <Field label="Display / brand name" error={errors.publicName}>
            <input style={inputStyle} value={form.publicName} onChange={(e) => set("publicName", e.target.value)} />
          </Field>
          <Field label="Business email" hint="Receives the Moyasar dashboard invitation." error={errors.email}>
            <input style={inputStyle} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Website or ordering link" hint="Must open and work — the reviewer checks it (a dead link sent Seven Seeds back)." error={errors.website}>
            <input style={inputStyle} placeholder="https://" value={form.website} onChange={(e) => set("website", e.target.value)} />
          </Field>
          <Field label="Statement descriptor (optional)" hint="Shown on the customer's card statement." error={errors.statement_descriptor}>
            <input style={inputStyle} value={form.statementDescriptor} onChange={(e) => set("statementDescriptor", e.target.value)} />
          </Field>
          <Field label="Number of owners on the CR" error={errors.ownersCount}>
            <input type="number" min="1" style={inputStyle} value={form.ownersCount} onChange={(e) => set("ownersCount", e.target.value)} />
          </Field>
          <Field label="Who signs the contract on Nafath" error={errors.signatory}>
            <select style={inputStyle} value={form.signatory} onChange={(e) => set("signatory", e.target.value)}>
              {Object.entries(SIGNATORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Card schemes" error={errors.enabled_schemes}>
            <Checks options={SCHEMES} value={form.enabledSchemes} onChange={(v) => set("enabledSchemes", v)} />
          </Field>
          <Field label="Payment methods" error={errors.payment_methods}>
            <Checks options={METHODS} value={form.paymentMethods} onChange={(v) => set("paymentMethods", v)} />
          </Field>
        </div>
        <div style={{ ...labelStyle, marginTop: 6 }}>Fees (Moyasar's charge to the merchant) — prefilled with the Wags standard rates</div>
        <FeesEditor fees={fees} setFees={setFees} errors={errors} />
        <div style={{ marginTop: 18 }}>
          <button style={btn("primary", saving)} disabled={saving} onClick={create}>{saving ? "Creating…" : "Create on Moyasar"}</button>
        </div>
      </RCard>

      <RCard title="…or link a merchant that already exists on Moyasar">
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input style={{ ...inputStyle, maxWidth: 420, fontFamily: "monospace" }} placeholder="Moyasar merchant ID (uuid)" value={linkId} onChange={(e) => setLinkId(e.target.value)} />
          <button style={btn("default", saving)} disabled={saving} onClick={link}>Link</button>
        </div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 6 }}>Moyasar merchants on this platform that nobody has linked are also listed on Merchants → Moyasar Platform.</div>
      </RCard>
    </Fragment>
  );
};

// ─── Tabs for a linked merchant ─────────────────────────────────────────────

const StatusTab = ({ ob, platform, busy, onSync, onSubmit }) => {
  const docsByType = useMemo(() => {
    const m = {};
    (ob.documentsSummary || []).forEach((d) => {
      m[d.type] = (m[d.type] || 0) + 1;
    });
    return m;
  }, [ob]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ob.signatureUrl);
      successToast("Link copied");
    } catch {
      errorToast("Could not copy — select the link and copy it");
    }
  };
  return (
    <Fragment>
      <RCard title="Moyasar status">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <StatusPill status={ob.status} />
          <span style={{ fontSize: 13, color: C.textMed }}>{STATUS_INFO[ob.status]?.help || "A status Moyasar added that this page does not know yet."}</span>
        </div>
        {(ob.reasons || []).length > 0 && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "#FDEDEC" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#C0392B", marginBottom: 6 }}>Why Moyasar sent it back</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
              {ob.reasons.map((r) => (
                <li key={r}>{REASON_LABELS[r] || r}</li>
              ))}
            </ul>
          </div>
        )}
        {ob.comment && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: C.orangeLight, fontSize: 13 }}>
            <b>Reviewer's comment:</b> <span dir="auto">{ob.comment}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button style={btn("primary", busy || !ob.canSubmitReview)} disabled={busy || !ob.canSubmitReview} onClick={onSubmit} title={ob.canSubmitReview ? "" : "Available when the status is Pending or Action needed and every required document is uploaded"}>
            Submit for review
          </button>
          <button style={btn("default", busy || ob.otherPlatform)} disabled={busy || ob.otherPlatform} onClick={onSync}>
            <RefreshCw size={14} /> Sync now
          </button>
          <span style={{ fontSize: 12, color: C.textLight, alignSelf: "center" }}>
            Last synced {ob.lastSyncedAt ? new Date(ob.lastSyncedAt).toLocaleString() : "never"}
          </span>
        </div>
      </RCard>

      <RCard title="Contract signing (Nafath)">
        <div style={{ fontSize: 13, marginBottom: 10 }}>{SIGNATURE_LABELS[ob.signatureStatus] || ob.signatureStatus || "—"}</div>
        {ob.signatureUrl ? (
          <Fragment>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input readOnly style={{ ...inputStyle, maxWidth: 560, fontFamily: "monospace", fontSize: 12 }} value={ob.signatureUrl} onFocus={(e) => e.target.select()} />
              <button style={btn("primary")} onClick={copy}><Copy size={14} /> Copy link</button>
              <a href={ob.signatureUrl} target="_blank" rel="noopener noreferrer" style={btn()}><ExternalLink size={14} /> Open</a>
            </div>
            <div style={{ fontSize: 12, color: C.textMed, marginTop: 8 }}>
              Send this link ONLY to the person who signs for the business. They open it on their phone and approve in the Nafath app with the number shown, using the mobile registered in Absher for their ID.
            </div>
          </Fragment>
        ) : (
          <div style={{ fontSize: 12, color: C.textMed }}>The link appears here while the merchant is under review, after Moyasar's checks pass.</div>
        )}
      </RCard>

      <RCard title="Required documents">
        <table style={tbl.table}>
          <tbody>
            {(ob.requiredDocuments || []).map((d) => {
              const have = docsByType[d.type] || 0;
              const done = have >= (d.required_count || 1);
              return (
                <tr key={d.type}>
                  <td style={{ ...tbl.td, width: 30, color: done ? "#1E8449" : "#C0392B", fontWeight: 800 }}>{done ? "✓" : "✗"}</td>
                  <td style={tbl.td}>{platform?.documentLabels?.[d.type] || d.type}</td>
                  <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>
                    {have}/{d.required_count || 1} · {d.requires_upload ? "file + details" : "details only"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ fontSize: 12, marginTop: 10, color: ob.documentsComplete ? "#1E8449" : C.textMed }}>
          {ob.documentsComplete ? "Moyasar marks the documents complete." : "Moyasar does not consider the documents complete yet."}
        </div>
      </RCard>
    </Fragment>
  );
};

const ProfileTab = ({ ob, onSaved, readOnly }) => {
  const [form, setForm] = useState({
    legalName: ob.legalName || "",
    publicName: ob.publicName || "",
    email: ob.adminEmail || "",
    website: ob.website || "",
    statementDescriptor: "",
    enabledSchemes: ob.enabledSchemes || [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    const body = {};
    if (form.legalName && form.legalName !== ob.legalName) body.legalName = form.legalName;
    if (form.publicName && form.publicName !== ob.publicName) body.publicName = form.publicName;
    if (form.email && form.email !== ob.adminEmail) body.email = form.email;
    if (form.website && form.website !== ob.website) body.website = form.website;
    if (form.statementDescriptor) body.statementDescriptor = form.statementDescriptor;
    if (JSON.stringify([...form.enabledSchemes].sort()) !== JSON.stringify([...(ob.enabledSchemes || [])].sort())) body.enabledSchemes = form.enabledSchemes;
    if (!Object.keys(body).length) return successToast("Nothing changed");
    setSaving(true);
    setErrors({});
    const res = await updateMoyasarMerchant(ob.merchantId, body);
    setSaving(false);
    if (!res.ok) {
      setErrors(formErrors(res.error.fieldErrors));
      return errorToast(res.error.message);
    }
    successToast("Saved on Moyasar");
    return onSaved();
  };

  return (
    <RCard title="Merchant details on Moyasar">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        <Field label="Legal name" error={errors.legalName}>
          <input style={inputStyle} dir="auto" disabled={readOnly} value={form.legalName} onChange={(e) => set("legalName", e.target.value)} />
        </Field>
        <Field label="Display / brand name" error={errors.publicName}>
          <input style={inputStyle} disabled={readOnly} value={form.publicName} onChange={(e) => set("publicName", e.target.value)} />
        </Field>
        <Field label="Business email" error={errors.email}>
          <input style={inputStyle} disabled={readOnly} value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label="Website or ordering link" hint="Must open and work for the reviewer." error={errors.website}>
          <input style={inputStyle} disabled={readOnly} value={form.website} onChange={(e) => set("website", e.target.value)} />
        </Field>
        <Field label="Statement descriptor" hint="Leave empty to keep the current one." error={errors.statement_descriptor}>
          <input style={inputStyle} disabled={readOnly} value={form.statementDescriptor} onChange={(e) => set("statementDescriptor", e.target.value)} />
        </Field>
        <Field label="Card schemes" error={errors.enabled_schemes}>
          <Checks options={SCHEMES} value={form.enabledSchemes} onChange={(v) => set("enabledSchemes", v)} disabled={readOnly} />
        </Field>
      </div>
      <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12 }}>
        Type: <b style={{ textTransform: "capitalize" }}>{ob.type || "—"}</b> · Moyasar ID: <span style={{ fontFamily: "monospace" }}>{ob.moyasarMerchantId}</span>
        {" · "}Publishable keys: live {ob.livePublishableKeySet ? "✓" : "—"}, test {ob.testPublishableKeySet ? "✓" : "—"}
      </div>
      <button style={btn("primary", saving || readOnly)} disabled={saving || readOnly} onClick={save}>{saving ? "Saving…" : "Save on Moyasar"}</button>
    </RCard>
  );
};

const FeesTab = ({ ob, onSaved, readOnly }) => {
  const initial = useMemo(() => feesFromRemote(ob.fees), [ob.fees]);
  const [fees, setFees] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    setErrors({});
    const res = await updateMoyasarMerchant(ob.merchantId, { fees: cleanFees(fees) });
    setSaving(false);
    if (!res.ok) {
      setErrors(res.error.fieldErrors || {});
      return errorToast(res.error.message);
    }
    successToast("Fees saved on Moyasar");
    return onSaved();
  };

  return (
    <RCard title="Moyasar processing fees">
      <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12 }}>
        What Moyasar charges this merchant per payment. Live Moyasar refuses refund fees below its platform minimum (1 SAR).
      </div>
      <FeesEditor fees={fees} setFees={setFees} errors={errors} disabled={readOnly} />
      <div style={{ marginTop: 16 }}>
        <button style={btn("primary", saving || readOnly)} disabled={saving || readOnly} onClick={save}>{saving ? "Saving…" : "Save fees on Moyasar"}</button>
      </div>
    </RCard>
  );
};

const DocumentForm = ({ ob, req, platform, onUploaded }) => {
  const [info, setInfo] = useState({});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const upload = async () => {
    setSaving(true);
    setErrors({});
    const res = await uploadMoyasarDocument(ob.merchantId, { type: req.type, info, file });
    setSaving(false);
    if (!res.ok) {
      setErrors(res.error.fieldErrors || {});
      return errorToast(res.error.message);
    }
    successToast("Uploaded to Moyasar");
    setInfo({});
    setFile(null);
    return onUploaded();
  };

  return (
    <div style={{ padding: 14, borderRadius: 10, border: `1px dashed ${C.border}`, marginTop: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        {req.info.map((field) => (
          <Field key={field} label={platform?.fieldLabels?.[field] || field} error={errors[`info.${field}`]}>
            <input
              style={inputStyle}
              dir="auto"
              type={field === "date_of_birth" ? "date" : "text"}
              value={info[field] || ""}
              onChange={(e) => setInfo({ ...info, [field]: e.target.value })}
            />
          </Field>
        ))}
        {req.requires_upload && (
          <Field label="File (PDF, JPG or PNG, up to 10 MB)" error={errors.file}>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Field>
        )}
      </div>
      <button style={btn("primary", saving)} disabled={saving} onClick={upload}>{saving ? "Uploading…" : "Upload to Moyasar"}</button>
    </div>
  );
};

const DocumentsTab = ({ ob, platform, onChanged, readOnly }) => {
  const [docs, setDocs] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    const res = await listMoyasarDocuments(ob.merchantId);
    if (res.ok) {
      setDocs(res.data.documents || []);
      setLoadError(null);
    } else setLoadError(res.error.message);
  }, [ob.merchantId]);

  useEffect(() => {
    if (!readOnly) load();
  }, [load, readOnly]);

  const remove = async (doc) => {
    if (!window.confirm(`Delete this ${platform?.documentLabels?.[doc.type] || doc.type} from Moyasar? You can upload a replacement afterwards.`)) return;
    const res = await deleteMoyasarDocument(ob.merchantId, doc.id);
    if (!res.ok) return errorToast(res.error.message);
    successToast("Deleted");
    await load();
    return onChanged();
  };

  const download = async (doc) => {
    const res = await downloadMoyasarDocument(ob.merchantId, doc.id);
    if (!res.ok) errorToast(res.error.message);
  };

  const refreshAll = async () => {
    await load();
    onChanged();
  };

  if (readOnly) return <RCard><div style={{ color: C.textMed }}>Documents are only available on the platform this merchant belongs to.</div></RCard>;
  if (loadError) return <RCard><div style={{ color: "#C0392B" }}>{loadError}</div></RCard>;
  if (!docs) return <RLoader />;

  return (
    <Fragment>
      {(ob.requiredDocuments || []).map((req) => {
        const uploaded = docs.filter((d) => d.type === req.type);
        const done = uploaded.length >= (req.required_count || 1);
        return (
          <RCard key={req.type} title={<span>{done ? "✓ " : ""}{platform?.documentLabels?.[req.type] || req.type}</span>}>
            {uploaded.map((d) => (
              <div key={d.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.borderLight}`, flexWrap: "wrap" }}>
                <div style={{ flex: 1, fontSize: 13 }} dir="auto">
                  {Object.entries(d.info || {}).map(([k, v]) => (
                    <span key={k} style={{ marginRight: 14 }}>
                      <span style={{ color: C.textMed }}>{platform?.fieldLabels?.[k] || k}:</span> {String(v)}
                    </span>
                  ))}
                  {!d.fileUploaded && req.requires_upload && <span style={{ color: "#C0392B" }}>no file</span>}
                </div>
                {d.fileUploaded && (
                  <button style={btn()} onClick={() => download(d)}><Download size={14} /> Download</button>
                )}
                <button style={btn("danger")} onClick={() => remove(d)}><Trash2 size={14} /> Delete</button>
              </div>
            ))}
            {!done && <DocumentForm ob={ob} req={req} platform={platform} onUploaded={refreshAll} />}
          </RCard>
        );
      })}
    </Fragment>
  );
};

// ─── Page ───────────────────────────────────────────────────────────────────

const MoyasarPlatformPage = () => {
  const { merchantID } = useParams();
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(null);
  const [state, setState] = useState(null); // { onboarding, platformId, syncError }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("status");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, o] = await Promise.all([getPlatformInfo(), getMoyasarOnboarding(merchantID)]);
    if (p.ok) setPlatform(p.data);
    if (o.ok) {
      setState(o.data);
      setError(null);
    } else setError(o.error.message);
    setLoading(false);
  }, [merchantID]);

  useEffect(() => {
    load();
  }, [load]);

  const ob = state?.onboarding;
  const linked = !!ob?.moyasarMerchantId;
  const readOnly = !!ob?.otherPlatform;

  const sync = async () => {
    setBusy(true);
    const res = await syncMoyasarMerchant(merchantID);
    setBusy(false);
    if (!res.ok) return errorToast(res.error.message);
    setState({ ...state, onboarding: res.data.onboarding, syncError: null });
    return successToast("Up to date");
  };

  const submit = async () => {
    if (!window.confirm("Submit this merchant to Moyasar for review? Their team reviews the documents and website.")) return;
    setBusy(true);
    const res = await submitMoyasarReview(merchantID);
    setBusy(false);
    if (!res.ok) return errorToast(res.error.message);
    setState({ ...state, onboarding: res.data.onboarding });
    successToast("Submitted for review");
  };

  return (
    <Fragment>
      <Breadcrumb title="Moyasar Onboarding" parent="Merchants" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <button style={btn()} onClick={() => navigate(`/merchant/edit-merchant/${merchantID}`)}>← Merchant</button>
          <button style={btn()} onClick={() => navigate("/merchant/moyasar-merchants")}>All Moyasar merchants</button>
          {platform?.platform && (
            <span style={{ fontSize: 12, color: C.textMed }}>
              Platform: <b>{platform.platform.name}</b> ({platform.baseUrl?.includes("apimig") ? "test" : "live"})
            </span>
          )}
        </div>

        {loading ? (
          <RLoader />
        ) : error ? (
          <RCard><div style={{ color: "#C0392B" }}>{error}</div></RCard>
        ) : platform && !platform.configured ? (
          <RCard><div style={{ color: "#C0392B", fontWeight: 600 }}>Moyasar Platform is not configured on this server (MOYASAR_PLATFORM_API_KEY).</div></RCard>
        ) : !linked ? (
          ob?.status === "creating" ? (
            <RCard>
              <div style={{ fontWeight: 600 }}>Creating on Moyasar…</div>
              <div style={{ fontSize: 13, color: C.textMed, marginTop: 6 }}>
                {ob.lastError?.message || "Waiting for Moyasar to confirm."} This page will show the merchant once it is confirmed; it is also checked automatically every hour.
              </div>
              <button style={{ ...btn(), marginTop: 12 }} onClick={load}><RefreshCw size={14} /> Check again</button>
            </RCard>
          ) : (
            <CreateOrLink merchantId={merchantID} platform={platform} previous={ob} onDone={(next) => { setTab(next); setLoading(true); load(); }} />
          )
        ) : (
          <Fragment>
            {readOnly && (
              <RCard>
                <div style={{ color: C.orange, fontWeight: 600 }}>
                  This merchant was onboarded on a different Moyasar platform (test vs live) than the one this server uses. It is shown read-only.
                </div>
              </RCard>
            )}
            {state?.syncError && (
              <RCard>
                <div style={{ color: "#C0392B", fontSize: 13 }}>Could not refresh from Moyasar ({state.syncError}). Showing the last known state.</div>
              </RCard>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {TABS.map((t) => (
                <button key={t.key} style={tabBtn(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
              ))}
            </div>
            {tab === "status" && <StatusTab ob={ob} platform={platform} busy={busy} onSync={sync} onSubmit={submit} />}
            {tab === "profile" && <ProfileTab key={ob.updatedAt} ob={ob} onSaved={load} readOnly={readOnly} />}
            {tab === "fees" && <FeesTab key={ob.updatedAt} ob={ob} onSaved={load} readOnly={readOnly} />}
            {tab === "documents" && <DocumentsTab ob={ob} platform={platform} onChanged={load} readOnly={readOnly} />}
          </Fragment>
        )}
      </Container>
    </Fragment>
  );
};

export default MoyasarPlatformPage;
